import { ActionIcon, Text, Tooltip, UnstyledButton } from "@mantine/core";
import { Download, Expand } from "lucide-react";
import { useState } from "react";

import { DownloadError, downloadSignedFile } from "../../api/download";
import type { Asset } from "../../api/generated/model";
import { SoundWave } from "../../ui/SoundWave";
import classes from "./sounds.module.css";

export interface SoundTileProps {
  asset: Asset;
  /** Its file plays in the mini player: the tile is lit and its bars move. */
  playing: boolean;
  onPlay: () => void;
  /** Opens the file in the library's lightbox (its ⤢ "Open preview", SA1 decision 8). */
  onPreview: () => void;
}

/**
 * One sound as live's tile shows it (captures 01, 05, 07): a square with the sound's bars and
 * its name; a click plays it; on hover a round button saves the file, and ⤢ in the top right
 * opens it in the lightbox.
 */
export function SoundTile({ asset, playing, onPlay, onPreview }: SoundTileProps) {
  const url = asset.urls.original;
  const [problem, setProblem] = useState<string | null>(null);
  const download = async () => {
    if (url === null) return;
    setProblem(null);
    try {
      await downloadSignedFile(url, asset.name);
    } catch (error) {
      const status = error instanceof DownloadError ? error.status : undefined;
      setProblem(
        status === undefined
          ? "The file could not be fetched here."
          : `The file could not be fetched (status ${status}).`,
      );
    }
  };
  return (
    <div className={classes.tile} data-playing={playing || undefined}>
      <UnstyledButton
        className={classes.tileButton}
        aria-label={`Listen to ${asset.name}`}
        aria-pressed={playing}
        disabled={url === null}
        onClick={onPlay}
      >
        <SoundWave className={classes.tileWave} animated={playing} />
        <Text className={classes.tileName} title={asset.name}>
          {asset.name}
        </Text>
      </UnstyledButton>
      {url === null ? null : (
        <Tooltip label="Open preview">
          <ActionIcon
            variant="text-white"
            className={`${classes.expand} ${classes.tileExpand}`}
            aria-label={`Open preview of ${asset.name}`}
            onClick={onPreview}
          >
            <Expand size={16} aria-hidden />
          </ActionIcon>
        </Tooltip>
      )}
      {url === null ? null : (
        <Tooltip label="Download">
          <ActionIcon
            className={classes.tileDownload}
            radius="xl"
            size={40}
            aria-label={`Download ${asset.name}`}
            onClick={() => void download()}
          >
            <Download size={18} />
          </ActionIcon>
        </Tooltip>
      )}
      {problem === null ? null : (
        <Text className={classes.tileProblem} role="alert">
          {problem}
        </Text>
      )}
    </div>
  );
}
